import Card from '@orn/shared-web/components/Card';
import { BankIcon } from '@orn/shared-web/icons';

import { CalloutPriority } from '../../../../__generated__/globalTypes';
import { toCurrency } from '../../../../utils/formatting';
import BankAccountNameAndAvatar from '../../../BankAccountNameAndAvatar';

import type { Callout } from '../../CalloutCard';

interface ILowBalanceMetadata {
  balance: number;
  threshold: number;
  repairReason?: 'threshold_changed' | 'balance_increased';
}

interface ILowBalanceCalloutCardProps {
  callout: Callout;
  /**
   * Called when the user dismisses the callout.
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismiss?: (appropriate?: boolean) => void;
  className?: string;
  onClick?: () => void;
  /**
   * 'full' shows full details (account, balance), used in carousel.
   * 'compact' shows description only, used in slideout.
   * @default 'full'
   */
  variant?: 'full' | 'compact';
}

export function isLowBalanceMetadata(metadata: unknown): metadata is ILowBalanceMetadata {
  if (typeof metadata !== 'object' || metadata === null || !('balance' in metadata) || !('threshold' in metadata)) {
    return false;
  }

  const { balance, threshold, repairReason } = metadata as ILowBalanceMetadata;

  if (typeof balance !== 'number' || typeof threshold !== 'number') {
    return false;
  }

  if (repairReason !== undefined && repairReason !== 'threshold_changed' && repairReason !== 'balance_increased') {
    return false;
  }

  return true;
}

function getDescription(metadata: ILowBalanceMetadata): string {
  const threshold = Math.round(metadata.threshold / 100);
  const formatted = `$${threshold.toLocaleString()}`;
  return `This account balance is less than ${formatted}.`;
}

const HighlightedDescription = ({ text }: { text: string }) => (
  <Card.Text>
    {text.split(/(\$[\d,]+(?:\.\d{2})?)/).map((part, index) => {
      if (part.match(/^\$[\d,]+(?:\.\d{2})?$/)) {
        return (
          <span key={index} className="text-primary font-bold">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    })}
  </Card.Text>
);

const LowBalanceCalloutCard = ({
  callout,
  onDismiss,
  className,
  onClick,
  variant = 'full',
}: ILowBalanceCalloutCardProps) => {
  // Validate metadata and ensure we have financial account info
  if (!isLowBalanceMetadata(callout.metadata) || !callout.financialAccount) {
    return null;
  }

  const metadata = callout.metadata;
  const financialAccount = callout.financialAccount;
  const isP0 = callout.priority === CalloutPriority.P0;
  const description = getDescription(metadata);

  // Normal state - show the callout card
  return (
    <Card variant={isP0 ? 'error' : 'warning'} onClick={onClick} className={className}>
      <Card.Header icon={<BankIcon />} label="Low balance" onDismiss={onDismiss ? () => onDismiss() : undefined} />
      {variant === 'full' ? (
        <Card.Body>
          {/* Row 1: Account (avatar + name + details) | Balance */}
          <div className="flex items-start justify-between">
            <BankAccountNameAndAvatar financialAccount={financialAccount} displayNameClassName="font-semibold" />
            <span className="text-md ml-2 font-semibold">{toCurrency(metadata.balance / 100)}</span>
          </div>
          {/* Row 2: Description */}
          <div className="flex flex-1 items-end">
            <HighlightedDescription text={description} />
          </div>
        </Card.Body>
      ) : (
        <Card.Body>
          <HighlightedDescription text={description} />
        </Card.Body>
      )}
    </Card>
  );
};

export default LowBalanceCalloutCard;
